import React, { createContext, useContext, useMemo } from "react";

import axios from "axios";

// =====================================================
// GLOBAL CONTEXT
// =====================================================

const GlobalContext = createContext(null);

// =====================================================
// GLOBAL PROVIDER
// =====================================================

export const GlobalProvider = ({ children }) => {
  // =====================================================
  // ENV CONFIG
  // =====================================================

  const packkyBaseUrl = process.env.REACT_APP_PACKKY_BASE_URL || "";

  const packkyUsername = process.env.REACT_APP_PACKKY_USERNAME || "";

  const packkyPassword = process.env.REACT_APP_PACKKY_PASSWORD || "";

  const anlookBaseUrl = process.env.REACT_APP_ANLOOK_BASE_URL || "";

  const anlookUsername = process.env.REACT_APP_ANLOOK_USERNAME || "";

  const anlookPassword = process.env.REACT_APP_ANLOOK_PASSWORD || "";

  const olaMapsApiKey = process.env.REACT_APP_OLA_MAPS_API_KEY || "";

  // =====================================================
  // BASIC AUTH TOKENS
  // =====================================================

  const packkyToken = useMemo(() => {
    if (!packkyUsername || !packkyPassword) {
      return "";
    }

    return btoa(`${packkyUsername}:${packkyPassword}`);
  }, [packkyUsername, packkyPassword]);

  const anlookToken = useMemo(() => {
    if (!anlookUsername || !anlookPassword) {
      return "";
    }

    return btoa(`${anlookUsername}:${anlookPassword}`);
  }, [anlookUsername, anlookPassword]);

  // =====================================================
  // PACKKY API INSTANCE
  // =====================================================

  const packkyApi = useMemo(() => {
    return axios.create({
      baseURL: packkyBaseUrl,

      headers: {
        Authorization: packkyToken ? `Basic ${packkyToken}` : "",

        "Content-Type": "application/json",

        Accept: "*/*",
      },
    });
  }, [packkyBaseUrl, packkyToken]);

  // =====================================================
  // ANLOOK API INSTANCE
  // =====================================================

  const anlookApi = useMemo(() => {
    return axios.create({
      baseURL: anlookBaseUrl,

      headers: {
        Authorization: anlookToken ? `Basic ${anlookToken}` : "",

        "Content-Type": "application/json",

        Accept: "*/*",
      },
    });
  }, [anlookBaseUrl, anlookToken]);

  // =====================================================
  // GLOBAL VALUES
  // =====================================================

  const value = useMemo(
    () => ({
      // APIs
      packkyApi,
      anlookApi,

      // Base URLs
      packkyBaseUrl,
      anlookBaseUrl,

      // Ola Maps
      olaMapsApiKey,
    }),
    [packkyApi, anlookApi, packkyBaseUrl, anlookBaseUrl, olaMapsApiKey],
  );

  // =====================================================
  // PROVIDER
  // =====================================================

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
};

// =====================================================
// CUSTOM GLOBAL CONTEXT HOOK
// =====================================================

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);

  if (!context) {
    throw new Error("useGlobalContext must be used inside GlobalProvider");
  }

  return context;
};

export default GlobalContext;
